import type {
  QueryBuilderState,
  QueryBuilderResult,
  WhereOperator,
  QueryBuilderTable,
} from '@/types/query-builder';

export function generateSQL(state: QueryBuilderState): QueryBuilderResult {
  const { tables, selectedColumns, joins, whereConditions, groupBy, having, orderBy, limit, offset, distinct } = state;

  if (tables.length === 0) {
    return { sql: '', params: [] };
  }

  const parts: string[] = [];
  const params: any[] = [];

  // SELECT clause
  parts.push('SELECT');
  if (distinct) {
    parts.push('DISTINCT');
  }

  if (selectedColumns.length === 0) {
    parts.push('*');
  } else {
    const columns = selectedColumns.map((col) => {
      const table = tables.find((t) => t.id === col.tableId);
      const tableName = table?.alias || table?.tableName || col.tableName;

      let columnExpr = `${tableName}.${col.columnName}`;

      if (col.aggregation) {
        if (col.aggregation === 'COUNT_DISTINCT') {
          columnExpr = `COUNT(DISTINCT ${tableName}.${col.columnName})`;
        } else {
          columnExpr = `${col.aggregation}(${tableName}.${col.columnName})`;
        }
      }

      if (col.alias) {
        columnExpr += ` AS ${col.alias}`;
      }

      return columnExpr;
    });

    parts.push(columns.join(', '));
  }

  // FROM clause
  const firstTable = tables[0];
  const fromClause = firstTable.alias
    ? `${firstTable.tableName} AS ${firstTable.alias}`
    : firstTable.tableName;
  parts.push('FROM', fromClause);

  // JOIN clauses
  for (const join of joins) {
    const rightTable = tables.find((t) => t.id === join.rightTableId);
    const leftTable = tables.find((t) => t.id === join.leftTableId);

    if (!rightTable || !leftTable) continue;

    const rightTableName = rightTable.alias || rightTable.tableName;
    const leftTableName = leftTable.alias || leftTable.tableName;

    const joinClause = rightTable.alias
      ? `${rightTable.tableName} AS ${rightTable.alias}`
      : rightTable.tableName;

    parts.push(
      `${join.type} JOIN`,
      joinClause,
      'ON',
      `${leftTableName}.${join.leftColumn} = ${rightTableName}.${join.rightColumn}`
    );
  }

  // WHERE clause
  if (whereConditions.length > 0) {
    parts.push('WHERE');
    const conditions = whereConditions.map((cond, index) => {
      const table = tables.find((t) => t.id === cond.tableId);
      const tableName = table?.alias || table?.tableName || '';
      const columnRef = `${tableName}.${cond.columnName}`;

      let conditionStr = '';

      if (index > 0) {
        conditionStr += ` ${cond.logic} `;
      }

      switch (cond.operator) {
        case 'equals':
          conditionStr += `${columnRef} = '${cond.value}'`;
          break;
        case 'not_equals':
          conditionStr += `${columnRef} != '${cond.value}'`;
          break;
        case 'greater_than':
          conditionStr += `${columnRef} > '${cond.value}'`;
          break;
        case 'less_than':
          conditionStr += `${columnRef} < '${cond.value}'`;
          break;
        case 'greater_or_equal':
          conditionStr += `${columnRef} >= '${cond.value}'`;
          break;
        case 'less_or_equal':
          conditionStr += `${columnRef} <= '${cond.value}'`;
          break;
        case 'like':
          conditionStr += `${columnRef} LIKE '%${cond.value}%'`;
          break;
        case 'not_like':
          conditionStr += `${columnRef} NOT LIKE '%${cond.value}%'`;
          break;
        case 'in':
          if (Array.isArray(cond.value)) {
            conditionStr += `${columnRef} IN (${cond.value.map(v => `'${v}'`).join(', ')})`;
          }
          break;
        case 'not_in':
          if (Array.isArray(cond.value)) {
            conditionStr += `${columnRef} NOT IN (${cond.value.map(v => `'${v}'`).join(', ')})`;
          }
          break;
        case 'is_null':
          conditionStr += `${columnRef} IS NULL`;
          break;
        case 'is_not_null':
          conditionStr += `${columnRef} IS NOT NULL`;
          break;
        case 'between':
          if (Array.isArray(cond.value) && cond.value.length === 2) {
            conditionStr += `${columnRef} BETWEEN '${cond.value[0]}' AND '${cond.value[1]}'`;
          }
          break;
      }

      return conditionStr;
    });

    parts.push(conditions.join(''));
  }

  // GROUP BY clause
  if (groupBy.length > 0) {
    parts.push('GROUP BY');
    const groupColumns = groupBy.map((col) => {
      const table = tables.find((t) => t.id === col.tableId);
      const tableName = table?.alias || table?.tableName || '';
      return `${tableName}.${col.columnName}`;
    });
    parts.push(groupColumns.join(', '));
  }

  // HAVING clause
  if (having.length > 0) {
    parts.push('HAVING');
    const havingConditions = having.map((cond, index) => {
      const table = tables.find((t) => t.id === cond.tableId);
      const tableName = table?.alias || table?.tableName || '';
      const aggFunc = cond.aggregation === 'COUNT_DISTINCT'
        ? `COUNT(DISTINCT ${tableName}.${cond.columnName})`
        : `${cond.aggregation}(${tableName}.${cond.columnName})`;

      let conditionStr = '';

      if (index > 0) {
        conditionStr += ` ${cond.logic} `;
      }

      conditionStr += `${aggFunc} ${getOperatorSymbol(cond.operator)} '${cond.value}'`;

      return conditionStr;
    });

    parts.push(havingConditions.join(''));
  }

  // ORDER BY clause
  if (orderBy.length > 0) {
    parts.push('ORDER BY');
    const orderColumns = orderBy.map((col) => {
      const table = tables.find((t) => t.id === col.tableId);
      const tableName = table?.alias || table?.tableName || '';
      return `${tableName}.${col.columnName} ${col.direction}`;
    });
    parts.push(orderColumns.join(', '));
  }

  // LIMIT clause
  if (limit !== undefined && limit > 0) {
    parts.push(`LIMIT ${limit}`);
  }

  // OFFSET clause
  if (offset !== undefined && offset > 0) {
    parts.push(`OFFSET ${offset}`);
  }

  const sql = parts.join(' ');

  return { sql, params };
}

function getOperatorSymbol(operator: WhereOperator): string {
  switch (operator) {
    case 'equals':
      return '=';
    case 'not_equals':
      return '!=';
    case 'greater_than':
      return '>';
    case 'less_than':
      return '<';
    case 'greater_or_equal':
      return '>=';
    case 'less_or_equal':
      return '<=';
    default:
      return '=';
  }
}

export function getOperatorLabel(operator: WhereOperator): string {
  switch (operator) {
    case 'equals':
      return 'Equals';
    case 'not_equals':
      return 'Not Equals';
    case 'greater_than':
      return 'Greater Than';
    case 'less_than':
      return 'Less Than';
    case 'greater_or_equal':
      return 'Greater or Equal';
    case 'less_or_equal':
      return 'Less or Equal';
    case 'like':
      return 'Contains';
    case 'not_like':
      return 'Not Contains';
    case 'in':
      return 'In List';
    case 'not_in':
      return 'Not In List';
    case 'is_null':
      return 'Is NULL';
    case 'is_not_null':
      return 'Is NOT NULL';
    case 'between':
      return 'Between';
    default:
      return operator;
  }
}
